import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export type StatusFilter =
  | 'all'
  | 'healthy'
  | 'soon'
  | 'expired'
  | 'drafts'
  | 'canceled'
  | 'archived'

export type PaymentFilter = 'all' | 'paid' | 'partial' | 'unpaid' | 'scheduled'

interface FilterContextValue {
  status: StatusFilter
  setStatus: (next: StatusFilter) => void
  payment: PaymentFilter
  setPayment: (next: PaymentFilter) => void
  /** Latest search input the user has typed (controlled value of the box). */
  searchInput: string
  setSearchInput: (next: string) => void
  /** Debounced version of `searchInput` consumed by useDealsQuery. */
  search: string
  /** Set of deal ids whose invoice sub-row is expanded. */
  expandedDealIds: ReadonlySet<string>
  toggleExpandDeal: (dealId: string) => void
  /** Replace the entire expanded set (used by search auto-expand effect). */
  setExpandedDealIds: (ids: string[]) => void
  isDealExpanded: (dealId: string) => boolean
}

const SEARCH_DEBOUNCE_MS = 250

const FilterContext = createContext<FilterContextValue | null>(null)

export const FilterProvider: React.FunctionComponent = (props) => {
  const [status, setStatus] = useState<StatusFilter>('all')
  const [payment, setPayment] = useState<PaymentFilter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const toggleExpandDeal = useCallback((dealId: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(dealId)) next.delete(dealId)
      else next.add(dealId)
      return next
    })
  }, [])

  const setExpandedDealIds = useCallback((ids: string[]) => {
    setExpanded(new Set(ids))
  }, [])

  const isDealExpanded = useCallback((dealId: string) => expanded.has(dealId), [expanded])

  const value = useMemo<FilterContextValue>(
    () => ({
      status,
      setStatus,
      payment,
      setPayment,
      searchInput,
      setSearchInput,
      search,
      expandedDealIds: expanded,
      toggleExpandDeal,
      setExpandedDealIds,
      isDealExpanded,
    }),
    [
      status,
      payment,
      searchInput,
      search,
      expanded,
      toggleExpandDeal,
      setExpandedDealIds,
      isDealExpanded,
    ],
  )

  return <FilterContext.Provider value={value}>{props.children}</FilterContext.Provider>
}

export function useFilters(): FilterContextValue {
  const context = useContext(FilterContext)
  if (!context) throw new Error('useFilters must be used inside <FilterProvider>')
  return context
}
